import React, { useEffect, useState } from 'react';
import { Layout, notification, Spin } from 'antd';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import './ResetPassword.scss';
import { OpDivider } from 'components/customAntd/DLS/OpDivider/OpDivider';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { useTranslation } from 'react-i18next';

const { Content } = Layout;
const apiUrl = process.env.REACT_APP_BACKEND_URL;

const ResetPassword: React.FC = () => {
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPasswordReset, setIsPasswordReset] = useState<boolean>(false);
    const { token } = useParams();
    const [form] = OpForm.useForm();

    const { t } = useTranslation();

    useEffect(() => {
        setIsLoading(true);
        if (token) {
            axios.post(`${apiUrl}/auth/accessTokens/${token}/validate`)
                .then(response => {
                    const { isValid } = response.data.data;
                    if (!isValid) {
                        setIsValid(false);
                        setIsLoading(false);
                        return;
                    }
                    setIsValid(true);
                })
                .catch(error => {
                    console.error('Token validation error:', error);
                    setIsValid(false);
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [token]);

    const handleSubmit = (async ({ values }: IOnSubmitArgs<any>) => {
        try {
            await axios.patch(`${apiUrl}/auth/changePassword`,
                {
                    password: values.newPassword
                },
                {
                    headers: {
                        Authorization: `${token}`
                    }
                }
            );
            notification.success({
                message: t('notification.success'),
                description: t('profile.password.success'),
                placement: 'bottomRight'
            });
            setIsPasswordReset(true);
        } catch (error) {
            console.error('Error submitting form:', error);
            notification.error({
                message: t('notification.error'),
                description: t('profile.password.error'),
                placement: 'bottomRight'
            });
        }
    });

    return (
        <Layout className="reset-password-layout">
            <Content className="reset-password-content">
                <img src="/images/Avigilon_logo_transp_sm.png" alt="Logo" className="logo" />
                {isLoading ? (
                    <div className="loading-container">
                        <Spin />
                    </div>
                ) : isPasswordReset ? (
                    <div className="success-message">
                        Your password has been saved.
                    </div>
                ) : isValid ? (
                    <OpForm
                        form={form}
                        name="exampleForm"
                        layout="vertical"
                        onSubmit={handleSubmit}
                        hasError={false}
                        defaultButtons={false}
                    >
                        <OpForm.PasswordInput
                            name='newPassword'
                            label='Reset Password'
                            placeholder="Enter new password"
                            rules={[
                                { required: true, message: 'Please enter your password' },
                                { min: 6, message: 'Password must be at least 6 characters' }
                            ]}
                        />
                        <div className="password-instructions">
                            Please enter at least 6 characters for your new password.
                        </div>
                        <OpDivider />
                        <OpButton type="primary" htmlType="submit">
                            Save
                        </OpButton>

                    </OpForm>
                ) : (
                    <div className="message-box">
                        <span>Invalid or expired link.</span>
                    </div>
                )}
            </Content>

            <footer className="reset-password-footer">
                &copy; {new Date().getFullYear()} Avigilon Corporation. All rights reserved.
            </footer>
        </Layout>
    );
};

export default ResetPassword;
